import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, mxn, fmtDate, formatApiError, exportToExcel } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Modal, Btn, Select, Input, EmptyState, ConfirmDialog, ListSkeleton } from "@/components/Kit";
import { StatusBadge, FolioTag } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { LineItemsEditor } from "@/components/LineItemsEditor";
import { TagsField, TagsCell } from "@/components/TagsInput";
import { IconBtn } from "@/pages/Cotizaciones";
import { FilterBar, matchFilters, sortByFolio, FolioSortHeader } from "@/components/FilterBar";
import { ImportDialog } from "@/components/ImportDialog";
import { PaymentModal } from "@/components/PaymentModal";
import { useRowSelection, SelectCheckbox, BulkDeleteBar } from "@/components/BulkSelect";
import { usePagination, Pagination } from "@/components/Pagination";
import { toast } from "sonner";
import { Plus, Printer, Banknote, Upload, Pencil, Ban, Link2, Download, CircleSlash, FileText } from "lucide-react";

export default function Facturas() {
  const navigate = useNavigate();
  const { canDo } = useAuth();
  const fCreate = canDo("facturas", "create");
  const fEdit = canDo("facturas", "edit");
  const fCancel = canDo("facturas", "cancel");
  const payCreate = canDo("cobranza", "create");
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [banks, setBanks] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(null);
  const [filters, setFilters] = useState({});
  const [folioDir, setFolioDir] = useState("desc");
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const [editInv, setEditInv] = useState(null);
  const [cancelInv, setCancelInv] = useState(null);
  const [editNumber, setEditNumber] = useState("");
  const [editItems, setEditItems] = useState([]);
  const [editIva, setEditIva] = useState(16);
  const [editClientId, setEditClientId] = useState("");
  const [editTags, setEditTags] = useState([]);

  const [linkInv, setLinkInv] = useState(null);
  const [linkableRems, setLinkableRems] = useState([]);
  const [linkSel, setLinkSel] = useState(new Set());
  const [linking, setLinking] = useState(false);
  const [linkAllClients, setLinkAllClients] = useState(false);
  const [linkIncludeInvoiced, setLinkIncludeInvoiced] = useState(false);
  const [linkConfirmOpen, setLinkConfirmOpen] = useState(false);

  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [ivaRate, setIvaRate] = useState(16);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [quoteId, setQuoteId] = useState("");
  const [editQuoteId, setEditQuoteId] = useState("");

  const [quotes, setQuotes] = useState([]);
  const [linkQuoteInv, setLinkQuoteInv] = useState(null);
  const [linkQuoteSel, setLinkQuoteSel] = useState("");
  const [savingQuoteLink, setSavingQuoteLink] = useState(false);

  const load = () => {
    api.get("/invoices").then((r) => setInvoices(r.data)).finally(() => setLoading(false));
    api.get("/clients").then((r) => setClients([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }))));
    api.get("/products?active=true").then((r) => setProducts(r.data));
    api.get("/payment-banks").then((r) => setBanks(r.data.map((b) => b.name)));
    api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => setQuotes([]));
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const eid = searchParams.get("edit");
    if (eid && invoices.length) {
      const inv = invoices.find((x) => x.id === eid);
      if (inv) {
        openEdit(inv);
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, invoices]);

  const createBank = async (name) => {
    try {
      await api.post("/payment-banks", { name });
      const r = await api.get("/payment-banks");
      setBanks(r.data.map((b) => b.name));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const createProduct = async (payload) => {
    try {
      const { data } = await api.post("/products", payload);
      setProducts((prev) => [data, ...prev]);
      toast.success("Producto creado");
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return null;
    }
  };

  const openNew = () => {
    setClientId(clients[0]?.id || "");
    setItems([{ description: "", quantity: 1, unit_price: 0 }]);
    setIvaRate(16);
    setDueDate("");
    setNotes("");
    setTags([]);
    setQuoteId("");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!clientId) return toast.error("Selecciona un cliente");
    if (!tags || tags.length === 0) return toast.error("Agrega al menos un tag (etiqueta) para guardar el documento");
    try {
      const { data } = await api.post("/invoices", { client_id: clientId, items, iva_rate: ivaRate, due_date: dueDate, notes, tags, quote_id: quoteId || null });
      toast.success("Factura creada");
      setOpen(false);
      if (data?.id) return navigate(`/documento/factura/${data.id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!deleteOpen) return;
    try {
      await api.delete(`/invoices/${deleteOpen.id}`);
      toast.success(`Factura ${deleteOpen.number} eliminada`);
      setDeleteOpen(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openEdit = (i) => {
    setEditInv(i);
    setEditNumber(i.number || "");
    setEditItems(i.items && i.items.length ? i.items : [{ description: "", quantity: 1, unit_price: 0 }]);
    setEditIva(i.iva_rate ?? 16);
    setEditClientId(i.client && i.client_id ? i.client_id : "");
    setEditTags(i.tags || []);
    setEditQuoteId(i.quote_id || "");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editNumber.trim()) return toast.error("El número de factura es obligatorio");
    const missingClient = !editInv.client;
    if (missingClient && !editClientId) return toast.error("Selecciona el cliente para asignarlo a la factura");
    if (!editTags || editTags.length === 0) return toast.error("Agrega al menos un tag (etiqueta) para guardar el documento");
    const clientIdToSave = missingClient ? editClientId : editInv.client_id;
    try {
      await api.put(`/invoices/${editInv.id}`, {
        number: editNumber.trim(),
        client_id: clientIdToSave,
        items: editItems,
        iva_rate: editIva,
        due_date: editInv.due_date || "",
        notes: editInv.notes || "",
        tags: editTags,
        quote_id: editQuoteId || null,
      });
      toast.success(`Factura ${editNumber.trim()} actualizada`);
      setEditInv(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openLinkQuote = (i) => {
    setLinkQuoteInv(i);
    setLinkQuoteSel(i.quote_id || "");
  };

  const saveQuoteLink = async () => {
    if (!linkQuoteInv) return;
    setSavingQuoteLink(true);
    try {
      await api.post(`/invoices/${linkQuoteInv.id}/link-quote`, { quote_id: linkQuoteSel || null });
      toast.success(linkQuoteSel ? "Cotización vinculada a la factura" : "Cotización desvinculada");
      setLinkQuoteInv(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingQuoteLink(false);
    }
  };

  const fetchLinkable = async (invId, all, includeInvoiced) => {
    try {
      const params = new URLSearchParams();
      if (all) params.set("all", "true");
      if (includeInvoiced) params.set("include_invoiced", "true");
      const qs = params.toString();
      const { data } = await api.get(`/invoices/${invId}/linkable-remisiones${qs ? `?${qs}` : ""}`);
      setLinkableRems(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openLink = async (i) => {
    setLinkInv(i);
    setLinkSel(new Set());
    setLinkableRems([]);
    setLinkAllClients(false);
    setLinkIncludeInvoiced(false);
    fetchLinkable(i.id, false, false);
  };

  const toggleAllClients = (checked) => {
    setLinkAllClients(checked);
    setLinkSel(new Set());
    if (linkInv) fetchLinkable(linkInv.id, checked, linkIncludeInvoiced);
  };

  const toggleIncludeInvoiced = (checked) => {
    setLinkIncludeInvoiced(checked);
    setLinkSel(new Set());
    if (linkInv) fetchLinkable(linkInv.id, linkAllClients, checked);
  };

  const toggleLinkRem = (id) =>
    setLinkSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectedFacturadas = () =>
    linkableRems.filter((r) => linkSel.has(r.id) && r.estado_facturacion === "facturada");

  const confirmLink = async () => {
    if (linkSel.size === 0) return toast.error("Selecciona al menos una remisión");
    if (selectedFacturadas().length > 0) {
      setLinkConfirmOpen(true);
      return;
    }
    await doLink();
  };

  const doLink = async () => {
    setLinkConfirmOpen(false);
    setLinking(true);
    try {
      const { data } = await api.post(`/invoices/${linkInv.id}/link-remisiones`, { remision_ids: [...linkSel] });
      toast.success(`${data.linked} remisión(es) relacionada(s)`);
      setLinkInv(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLinking(false);
    }
  };

  const cancelInvoice = async () => {
    if (!cancelInv) return;
    try {
      await api.post(`/invoices/${cancelInv.id}/cancel`);
      toast.success(`Factura ${cancelInv.number} anulada. Las remisiones asociadas regresaron a pendiente.`);
      setCancelInv(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const bulkRemove = async () => {
    try {
      await api.post(`/invoices/bulk-delete`, { ids: sel.selectedIds });
      toast.success(`${sel.selectedIds.length} facturas eliminadas`);
      sel.clear();
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filteredInvoices = useMemo(
    () => sortByFolio(invoices.filter((i) => matchFilters(i, filters)), folioDir),
    [invoices, filters, folioDir]
  );

  const sel = useRowSelection(filteredInvoices);
  const pageInvoices = usePagination(filteredInvoices, 50, { persist: true });

  const exportExcel = async () => {
    try {
      const headers = ["Folio", "Cliente", "Remisión", "Tags", "Fecha", "Vencimiento", "Total", "Saldo", "Estatus"];
      const rows = filteredInvoices.map((i) => [
        i.number, i.client?.name || "", (i.remision_numbers || []).join(", "),
        (i.tags || []).join(", "), fmtDate(i.created_at), fmtDate(i.due_date),
        Number(i.total || 0), Number(i.balance || 0), i.status || "",
      ]);
      const by = {};
      let tc = 0, tt = 0, ts = 0;
      filteredInvoices.forEach((i) => {
        const s = i.status || "Sin estatus";
        by[s] = by[s] || { c: 0, t: 0, s: 0 };
        by[s].c++; by[s].t += Number(i.total || 0); by[s].s += Number(i.balance || 0);
        tc++; tt += Number(i.total || 0); ts += Number(i.balance || 0);
      });
      const summaryRows = Object.entries(by).map(([s, v]) => [s, v.c, Number(v.t.toFixed(2)), Number(v.s.toFixed(2))]);
      summaryRows.push(["TOTAL GENERAL", tc, Number(tt.toFixed(2)), Number(ts.toFixed(2))]);
      const summary = { title: "Resumen", headers: ["Estatus", "Cantidad", "Total", "Saldo"], rows: summaryRows };
      await exportToExcel("facturas", "Facturas", headers, rows, summary);
    } catch {
      toast.error("No se pudo exportar a Excel");
    }
  };

  return (
    <>
      <PageHeader
        overline="Ciclo de venta · Paso 3"
        title="Facturación"
        action={
          <div className="flex gap-3">
            <Btn variant="outline" onClick={exportExcel} data-testid="export-facturas-button" disabled={invoices.length === 0}>
              <Download size={16} /> Exportar a Excel
            </Btn>
            {fCreate && (
              <Btn variant="outline" onClick={() => setImportOpen(true)} data-testid="import-facturas-button">
                <Upload size={16} /> Importar
              </Btn>
            )}
            {fCreate && (
              <Btn onClick={openNew} data-testid="new-invoice-button" disabled={clients.length === 0}>
                <Plus size={16} /> Nueva factura
              </Btn>
            )}
          </div>
        }
      />
      <div className="p-10">
        {loading ? (
          <ListSkeleton testid="facturas-loading" />
        ) : invoices.length === 0 ? (
          <EmptyState
            title="Sin facturas aún"
            subtitle="Genera una factura desde una orden de trabajo o crea una manualmente."
            action={fCreate && clients.length > 0 && <Btn onClick={openNew}><Plus size={16} /> Nueva factura</Btn>}
          />
        ) : (
          <>
          <FilterBar clients={clients} reportType="facturas" onApply={setFilters} statuses={[...new Set(invoices.map((i) => i.status).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"))} showRemision showTags />
          {fCancel && <BulkDeleteBar count={sel.selectedIds.length} onDelete={() => setBulkOpen(true)} testid="bulk-delete-facturas" />}
          <div className="border border-[#E3E8EE] bg-[#FFFFFF] data-scroll">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3E8EE] text-left">
                  <Th className="w-10">{fCancel && <SelectCheckbox checked={sel.allChecked} onChange={sel.toggleAll} testid="select-all-facturas" />}</Th>
                  <Th><FolioSortHeader dir={folioDir} onToggle={() => setFolioDir((d) => d === "desc" ? "asc" : "desc")} /></Th>
                  <Th>Cliente</Th>
                  <Th>Remisión</Th>
                  <Th>Cotización</Th>
                  <Th>Tags</Th>
                  <Th>Fecha</Th>
                  <Th>Vencimiento</Th>
                  <Th className="text-right">Total</Th>
                  <Th className="text-right">Saldo</Th>
                  <Th>Estatus</Th>
                  <Th className="text-right">Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {pageInvoices.pageItems.map((i) => (
                  <tr key={i.id} data-testid={`invoice-row-${i.number}`} onClick={() => navigate(`/documento/factura/${i.id}`)} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3] transition-colors cursor-pointer">
                    <Td onClick={(e) => e.stopPropagation()}>{fCancel && <SelectCheckbox checked={sel.selected.has(i.id)} onChange={() => sel.toggle(i.id)} testid={`select-invoice-${i.number}`} />}</Td>
                    <Td className="text-[#1C1A17] font-medium">{i.number}</Td>
                    <Td>{i.client?.name || "—"}</Td>
                    <Td data-testid={`invoice-remision-${i.number}`}>
                      {i.remision_numbers?.length
                        ? <span className="inline-flex flex-wrap gap-1">{i.remision_numbers.map((n) => <FolioTag key={n} number={n} kind="remision" />)}</span>
                        : "—"}
                    </Td>
                    <Td data-testid={`invoice-quote-${i.number}`}>
                      {i.quote_number
                        ? <FolioTag number={i.quote_number} kind="quote" />
                        : "—"}
                    </Td>
                    <Td><TagsCell tags={i.tags} testid={`invoice-tags-${i.number}`} /></Td>
                    <Td>{fmtDate(i.created_at)}</Td>
                    <Td>{fmtDate(i.due_date)}</Td>
                    <Td className="text-right text-[#1C1A17]">{mxn(i.total)}</Td>
                    <Td className="text-right" style={{ color: i.balance > 0 ? "#ED1F26" : "#49AC9A" }}>{mxn(i.balance)}</Td>
                    <Td><StatusBadge status={i.status} /></Td>
                    <Td className="text-right whitespace-nowrap">
                      <div onClick={(e) => e.stopPropagation()}>
                      {payCreate && i.balance > 0 && i.status !== "Cancelada" && (
                        <IconBtn title="Aplicar pago" onClick={() => setPayOpen(i)} testid={`pay-invoice-${i.number}`}><Banknote size={15} /></IconBtn>
                      )}
                      {fEdit && <IconBtn title="Editar" disabled={i.status === "Cancelada"} onClick={() => openEdit(i)} testid={`edit-invoice-${i.number}`}><Pencil size={15} /></IconBtn>}
                      {fEdit && i.status !== "Cancelada" && (
                        <IconBtn title="Relacionar remisiones" onClick={() => openLink(i)} testid={`link-invoice-${i.number}`}><Link2 size={15} /></IconBtn>
                      )}
                      {fEdit && i.status !== "Cancelada" && (
                        <IconBtn title="Vincular cotización" onClick={() => openLinkQuote(i)} testid={`link-quote-invoice-${i.number}`}><FileText size={15} /></IconBtn>
                      )}
                      {fCancel && i.status !== "Cancelada" && (
                        <IconBtn title="Anular factura" onClick={() => setCancelInv(i)} testid={`cancel-invoice-${i.number}`}><Ban size={15} /></IconBtn>
                      )}
                      <a href={`/imprimir/factura/${i.id}`} target="_blank" rel="noreferrer" className="inline-block text-[#5C5A55] hover:text-[#465B7F] mx-1.5" title="PDF" data-testid={`print-invoice-${i.number}`}><Printer size={15} /></a>
                      {fCancel && <IconBtn title="Anular Documento" tone="anular" onClick={() => setDeleteOpen(i)} testid={`delete-invoice-${i.number}`}><CircleSlash size={15} /></IconBtn>}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination {...pageInvoices} testid="pagination-facturas" />
          </>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Nueva factura" xl logo>
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Select label="Cliente *" value={clientId} onChange={(e) => setClientId(e.target.value)} testid="invoice-client-select">
              <option value="">Selecciona…</option>
              {[...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Input label="Vencimiento" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} testid="invoice-due-date" />
          </div>
          <LineItemsEditor items={items} setItems={setItems} ivaRate={ivaRate} setIvaRate={setIvaRate} products={products} onCreateProduct={createProduct} />
          <QuotePicker quotes={quotes} value={quoteId} onChange={setQuoteId} testid="invoice-quote-picker" />
          <label className="block">
            <span className="overline block mb-1.5">Notas</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="invoice-notes"
              className="w-full bg-[#FFFFFF] border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <TagsField tags={tags} setTags={setTags} testid="invoice-tags-input" />
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-invoice-button">Crear factura</Btn>
          </div>
        </form>
      </Modal>

      <PaymentModal
        open={!!payOpen}
        onClose={() => setPayOpen(null)}
        target={payOpen ? { id: payOpen.id, number: payOpen.number, balance: payOpen.balance, type: "factura" } : null}
        banks={banks}
        onCreateBank={createBank}
        onSaved={load}
      />

      <Modal open={!!deleteOpen} onClose={() => setDeleteOpen(null)} title="Anular factura">
        <p className="font-mono-data text-sm text-[#5C5A55]">
          ¿Anular la factura <span className="text-[#1C1A17]">{deleteOpen?.number}</span>? Cambiará a estatus "Anulado" y conservará su folio (no se elimina de la base de datos); las remisiones asociadas regresarán a pendiente.
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <Btn variant="ghost" type="button" onClick={() => setDeleteOpen(null)}>Cancelar</Btn>
          <Btn variant="danger" type="button" onClick={remove} data-testid="confirm-delete-invoice-button">Anular factura</Btn>
        </div>
      </Modal>

      <ConfirmDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onConfirm={bulkRemove}
        title="Anular facturas"
        message={`¿Anular ${sel.selectedIds.length} factura(s)? Cambiarán a estatus "Anulado" y conservarán su folio.`}
        confirmLabel="Anular seleccionados"
        testid="confirm-bulk-delete-facturas-button"
      />

      <ConfirmDialog open={!!cancelInv} onClose={() => setCancelInv(null)} onConfirm={cancelInvoice}
        title="Anular factura"
        message={`¿Anular la factura ${cancelInv?.number}? Se marcará como "Cancelada" (se conserva para historial) y las remisiones asociadas regresarán a "Pendiente por facturar".`}
        confirmLabel="Anular factura" testid="confirm-cancel-invoice-button" />

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} module="invoices" title="Importar facturas" onDone={load} />

      <Modal open={!!linkInv} onClose={() => setLinkInv(null)} title={`Relacionar remisiones · ${linkInv?.number || ""}`} wide>
        <div className="space-y-4">
          <p className="font-mono-data text-sm text-[#5B6672]">
            Selecciona <span className="text-[#16202B]">una o varias</span> remisiones pendientes por facturar del cliente <span className="text-[#16202B]">{linkInv?.client?.name || ""}</span>. Cada remisión <span className="text-[#16202B]">conserva su propio importe</span>; la factura conserva su total ({mxn(linkInv?.total || 0)}).
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={linkAllClients} onChange={(e) => toggleAllClients(e.target.checked)}
                data-testid="link-all-clients" className="w-4 h-4 accent-[#465B7F]" />
              <span className="font-mono-data text-xs text-[#5B6672]">Mostrar remisiones de todos los clientes</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={linkIncludeInvoiced} onChange={(e) => toggleIncludeInvoiced(e.target.checked)}
                data-testid="link-include-invoiced" className="w-4 h-4 accent-[#465B7F]" />
              <span className="font-mono-data text-xs text-[#5B6672]">Incluir remisiones ya facturadas (dividir en varias facturas)</span>
            </label>
          </div>
          {linkableRems.length === 0 ? (
            <p className="font-mono-data text-sm text-[#5C5A55] py-6 text-center border border-[#E3E8EE]" data-testid="no-linkable-remisiones">
              No hay remisiones {linkIncludeInvoiced ? "" : "pendientes por facturar "}{linkAllClients ? "" : "para este cliente"}.
            </p>
          ) : (
            <div className="border border-[#E3E8EE] divide-y divide-[#E3E8EE] max-h-72 overflow-auto" data-testid="linkable-remisiones-list">
              {linkableRems.map((r) => {
                const yaFact = r.estado_facturacion === "facturada";
                return (
                <label key={r.id} data-testid={`linkable-remision-${r.number}`}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#E9EEF3]">
                  <input type="checkbox" checked={linkSel.has(r.id)} onChange={() => toggleLinkRem(r.id)}
                    data-testid={`linkable-remision-check-${r.number}`} className="h-4 w-4 accent-[#465B7F]" />
                  <span className="font-mono-data text-sm text-[#16202B] w-24">{r.number}</span>
                  <span className="font-mono-data text-xs text-[#5B6672] flex-1 truncate">{r.client?.name || "—"}</span>
                  {yaFact && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#FDC50D]/20 text-[#8a6d00] border border-[#FDC50D]/40 whitespace-nowrap" title={`Ya facturada en ${r.factura_number || ""}`}>
                      Ya en {r.factura_number || "factura"}
                    </span>
                  )}
                  <span className="font-mono-data text-xs text-[#5B6672]">{fmtDate(r.created_at)}</span>
                  <span className="font-mono-data text-sm text-[#16202B]">{mxn(r.total)}</span>
                </label>
                );
              })}
            </div>
          )}
          <div className="flex justify-between items-center font-mono-data text-sm">
            <span className="text-[#5B6672]">
              {linkSel.size} remisión(es) seleccionada(s) · Suma: <span className="text-[#16202B]">{mxn(linkableRems.filter((r) => linkSel.has(r.id)).reduce((s, r) => s + (r.total || 0), 0))}</span>
            </span>
            <span className="text-[#5B6672]">Total factura: <span className="text-[#16202B]">{mxn(linkInv?.total || 0)}</span></span>
          </div>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setLinkInv(null)}>Cancelar</Btn>
            <Btn type="button" onClick={confirmLink} disabled={linking || linkSel.size === 0} data-testid="confirm-link-remisiones-button">
              {linking ? "Relacionando…" : "Relacionar"}
            </Btn>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={linkConfirmOpen}
        onClose={() => setLinkConfirmOpen(false)}
        onConfirm={doLink}
        title="Remisión ya facturada"
        message={`Seleccionaste ${selectedFacturadas().length} remisión(es) que ya están facturadas (${selectedFacturadas().map((r) => r.number).join(", ")}). Se relacionarán TAMBIÉN con la factura ${linkInv?.number || ""} (una remisión con varias facturas). ¿Deseas continuar?`}
        confirmLabel="Sí, relacionar"
        testid="confirm-link-facturada-button"
      />


      <Modal open={!!linkQuoteInv} onClose={() => setLinkQuoteInv(null)} title={`Vincular cotización · ${linkQuoteInv?.number || ""}`} wide>
        <div className="space-y-4">
          <p className="font-mono-data text-xs text-[#5B6672]">
            Vincula esta factura a una cotización (presupuesto) como alternativa al flujo por remisión. Es opcional.
          </p>
          <QuotePicker quotes={quotes} value={linkQuoteSel} onChange={setLinkQuoteSel} testid="linkquote-picker" />
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setLinkQuoteInv(null)}>Cancelar</Btn>
            <Btn onClick={saveQuoteLink} disabled={savingQuoteLink} data-testid="save-link-quote-button">{savingQuoteLink ? "Guardando…" : "Guardar vínculo"}</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!editInv} onClose={() => setEditInv(null)} title={`Editar factura ${editInv?.number || ""}`} xl logo>
        <form onSubmit={saveEdit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Número de factura *" value={editNumber} onChange={(e) => setEditNumber(e.target.value)} testid="edit-invoice-number" required />
            {editInv && !editInv.client ? (
              <Select label="Cliente (sin asignar) *" value={editClientId} onChange={(e) => setEditClientId(e.target.value)} testid="edit-invoice-client-select">
                <option value="">Selecciona…</option>
                {[...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            ) : (
              <label className="block">
                <span className="overline block mb-1.5">Cliente</span>
                <div className="w-full bg-[#F4F6F9] border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm text-[#5C5A55]" data-testid="edit-invoice-client-readonly">
                  {editInv?.client?.name || "—"}
                </div>
              </label>
            )}
          </div>
          <LineItemsEditor items={editItems} setItems={setEditItems} ivaRate={editIva} setIvaRate={setEditIva} products={products} onCreateProduct={createProduct} />
          <QuotePicker quotes={quotes} value={editQuoteId} onChange={setEditQuoteId} testid="edit-invoice-quote-picker" />
          <TagsField tags={editTags} setTags={setEditTags} testid="edit-invoice-tags-input" />
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setEditInv(null)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-edit-invoice-button">Guardar cambios</Btn>
          </div>
        </form>
      </Modal>
    </>
  );
}

function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "", style, onClick }) {
  return <td onClick={onClick} style={style} className={`font-mono-data text-sm text-[#5C5A55] px-4 py-3 ${className}`}>{children}</td>;
}

function QuotePicker({ quotes, value, onChange, testid = "quote-picker" }) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const list = (quotes || []).filter((c) => {
    if (!term) return true;
    const nm = (c.number || "").toLowerCase();
    const cl = (c.client?.name || c.client_name || "").toLowerCase();
    return nm.includes(term) || cl.includes(term);
  }).slice(0, 60);
  const selected = (quotes || []).find((c) => c.id === value);
  return (
    <div data-testid={testid}>
      <span className="overline block mb-1.5">Cotización vinculada <span className="normal-case text-[#8A94A0]">(opcional)</span></span>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar por folio o cliente…"
        data-testid={`${testid}-search`}
        className="w-full bg-white border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
      />
      <div className="border border-[#E3E8EE] max-h-44 overflow-auto mt-2 divide-y divide-[#EEF2F6]">
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid={`${testid}-none`}
          className={`w-full text-left px-3 py-2 font-mono-data text-sm transition-colors ${!value ? "bg-[#465B7F] text-white" : "hover:bg-[#465B7F]/10 text-[#5C5A55]"}`}
        >
          Sin cotización
        </button>
        {list.map((c) => (
          <button
            type="button"
            key={c.id}
            onClick={() => onChange(c.id)}
            data-testid={`${testid}-opt-${c.number}`}
            className={`w-full text-left px-3 py-2 font-mono-data text-sm transition-colors ${value === c.id ? "bg-[#465B7F] text-white" : "hover:bg-[#465B7F]/10 text-[#16202B]"}`}
          >
            <span className="font-semibold">{c.number}</span> · {c.client?.name || c.client_name || "—"} · {mxn(c.total)}
          </button>
        ))}
        {list.length === 0 && <div className="px-3 py-2 font-mono-data text-xs text-[#8A94A0]">Sin resultados.</div>}
      </div>
      {selected && (
        <p className="font-mono-data text-xs text-[#465B7F] mt-1.5" data-testid={`${testid}-selected`}>
          Vinculada: {selected.number} · {selected.client?.name || selected.client_name || "—"}
        </p>
      )}
    </div>
  );
}
